import React, { useContext, useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Modal, Spinner, Form, FormControl, Button, Card } from "react-bootstrap";
import DataTable from 'react-data-table-component';
import downloadCSV from '../csv/downloadCSV';
import Select from 'react-select';
import consultaEstadoDocs from 'api/consultaEstadoDocs';
import { AccountContext } from 'components/Account/Account';
import consultaPorEstado from "api/consultaPorEstadoDocs";
import consultaEstado from 'api/consultaEstado';
import reenvioDocs from "api/reenvioDocs";
import { TIPOS_DOCUMENTO_CON_TODOS } from "constants/tiposDocumento";
import { getOrigenLabel } from "constants/origenEmision";

// Documentos por llamada a reenvioDocs. La lambda los procesa de a uno (~45 ms
// cada uno) y API Gateway corta la respuesta a los 29 s: con mas de ~600 el
// navegador recibia un error aunque el reenvio terminara bien (10-09-2026: 1.426
// reenviados en 65 s y el panel mostro falla). Una tanda de 200 tarda ~9 s.
const TANDA_REENVIO = 200;

const cantidadTandas = (n) => Math.ceil(n / TANDA_REENVIO);
const miles = (n) => Number(n || 0).toLocaleString('es-CL');

// Las tandas en que se parte una selección, para mostrarlas ANTES de enviar y
// seguirlas mientras se envían. Numeradas desde 1, como las lee una persona.
const armarTandas = (total) => Array.from({ length: cantidadTandas(total) }, (_, i) => ({
  n: i + 1,
  desde: i * TANDA_REENVIO + 1,
  hasta: Math.min((i + 1) * TANDA_REENVIO, total),
  estado: 'pendiente',
  reenviados: 0,
  fallidos: 0,
}));

// Paleta corporativa (#054A91 / #DBE4EE) para lo informativo; verde, ámbar y
// rojo solo como semáforo, con los tonos de .count-cell de esta misma vista.
const AZUL = '#054A91';
const AZUL_NIEBLA = '#DBE4EE';
const ESTADO_TANDA = {
  pendiente: { texto: 'pendiente', fondo: '#f1f5f9', color: '#64748b' },
  enviando: { texto: 'enviando…', fondo: AZUL_NIEBLA, color: AZUL },
  ok: { texto: 'ok', fondo: '#d1fae5', color: '#047857' },
  con_fallidos: { texto: 'con fallidos', fondo: '#fef3c7', color: '#92400e' },
  sin_respuesta: { texto: 'sin respuesta', fondo: '#fee2e2', color: '#b91c1c' },
  no_enviada: { texto: 'no enviada', fondo: '#f1f5f9', color: '#64748b' },
};

// Etiqueta de estado de una tanda: todas del mismo ancho y alto, para que la
// columna quede pareja.
function EstadoTanda({ tanda }) {
  const e = ESTADO_TANDA[tanda.estado];
  const detalle = ['ok', 'con_fallidos'].includes(tanda.estado)
    ? ` · ${miles(tanda.reenviados)}${tanda.fallidos ? ` + ${miles(tanda.fallidos)} ✗` : ''}`
    : '';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 150, height: 22, padding: '0 10px', borderRadius: 6,
      background: e.fondo, color: e.color, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      {e.texto}{detalle}
    </span>
  );
}

// Lista de tandas con su estado y, arriba, la barra de avance. Se usa igual en
// la confirmación (todas «pendiente») y durante el envío.
function ListaTandas({ tandas, total, conBarra }) {
  // Solo cuenta lo que respondió: de una tanda sin respuesta no se sabe si la
  // lambda la procesó.
  const procesados = tandas
    .filter((t) => ['ok', 'con_fallidos'].includes(t.estado))
    .reduce((s, t) => s + (t.hasta - t.desde + 1), 0);
  const enCurso = tandas.find((t) => t.estado === 'enviando');
  const pct = total ? Math.round((100 * procesados) / total) : 0;
  return (
    <div>
      {conBarra && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600, color: AZUL, marginBottom: 6 }}>
            <span>{enCurso ? `Tanda ${enCurso.n} de ${tandas.length}` : `${tandas.length} tanda${tandas.length === 1 ? '' : 's'}`}</span>
            <span>{miles(procesados)} de {miles(total)} documentos</span>
          </div>
          <div style={{ height: 8, background: AZUL_NIEBLA, borderRadius: 4, overflow: 'hidden' }}>
            <div style={{ width: `${pct}%`, height: '100%', background: AZUL, transition: 'width 0.3s ease' }} />
          </div>
        </>
      )}
      <div style={{ maxHeight: 260, overflowY: 'auto', marginTop: conBarra ? 12 : 0, border: `1px solid ${AZUL_NIEBLA}`, borderRadius: 8, textAlign: 'left' }}>
        {tandas.map((t) => (
          <div key={t.n} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '6px 12px', borderTop: t.n === 1 ? 'none' : '1px solid #eef2f7', fontSize: 13,
            background: t.estado === 'enviando' ? '#f7f9fc' : 'transparent',
          }}>
            <span style={{ color: '#1e293b', fontVariantNumeric: 'tabular-nums' }}>
              <strong>Tanda {t.n}</strong>
              <span style={{ color: '#64748b' }}> · documentos {miles(t.desde)} a {miles(t.hasta)}</span>
            </span>
            <EstadoTanda tanda={t} />
          </div>
        ))}
      </div>
    </div>
  );
}

function EstadoResumen() {

  let today = new Date();
  let miliseconds = today.getTime() - 24 * 60 * 60 * 1000;//restar 2 días
  let yesterday = new Date(miliseconds);
  const [tipodocumento, setTipodocumento] = useState("39");
  const [startDate, setStartDate] = useState(new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [filtered, setFiltered] = useState(null)//Filtro para eliminar elementos nulos y asignar solo atributos vistos en tabla
  const [filter, setFilter] = useState('');//value input
  const [dataFiltered, setDataFiltered] = useState(null);
  const [dataDocs, setDataDocs] = useState(null);//datos filtrados del input search
  const [Message, setMessage] = useState('');
  const [modalTitle, setmodalTitle] = useState('');

  const { user, getSession } = useContext(AccountContext);
  const [checked, setChecked] = useState(false);
  const [show, setShow] = useState(false);
  const [detailShow, setDetailShow] = useState(false);
  const [detailData, setDetailData] = useState(false);
  const [selectedRows, setSelectedRows] = React.useState([]);
  const [detailError, setDetailError] = useState('');
  const [toggleCleared, setToggleCleared] = React.useState(false);
  // Reenvío por tandas: la confirmación (qué se va a mandar y en cuántas
  // tandas) y el avance de cada tanda mientras se manda.
  const [confirmacion, setConfirmacion] = useState(null);
  const [tandasReenvio, setTandasReenvio] = useState([]);
  const [totalReenvio, setTotalReenvio] = useState(0);

  const handleClose = () => {
    setShow(false)
    setTandasReenvio([]);
  };
  const handleCloseDetail = () => {
    setDetailShow(false)
    setConfirmacion(null);
  };

  const cleanParams = (obj) =>
    Object.fromEntries(
      Object.entries(obj).filter(([, v]) => v !== '' && v !== null && v !== undefined)
    );
  const busqueda = async (e) => {
    e.preventDefault()


    setMessage('Cargando');
    setLoading(true);
    setmodalTitle('Consulta Estado Resumen')
    setShow(true);

    console.log('busqueda')

    const params = cleanParams({ startDate, endDate, tipodocumento });

    console.log('params', params);

    const idToken = await getSession().then((session) => { return session.idToken.jwtToken });
    // console.log(params,idToken);
    try {
      const resp = await consultaEstadoDocs(params, idToken);
      setFiltered(resp);

      setLoading(false);
      setShow(false);


    } catch (error) {
      console.log(error)
      setMessage('Error');
      setLoading(false);
    }




    //Consultar a Api por documento (folio,rutEmpresa)
  }

  const handleButtonClick = async (row, estado) => {

    let RUT = row.rut;

    setmodalTitle('Consulta Estado Resumen')
    setMessage('Cargando');
    setLoading(true);

    setShow(true);

    // Sin esto el modal se abre con el resultado de la consulta anterior cuando
    // la nueva falla, mostrando documentos que no corresponden al conteo pulsado.
    setDetailData(null);
    setDetailError('');
    setSelectedRows([]);

    try {
      const idToken = await getSession().then((session) => { return session.idToken.jwtToken });
      const params = { estado, RUT, startDate, endDate, tipodocumento };
      const resp = await consultaPorEstado(params, idToken);
      console.log('resp', resp)
      setDetailData(Array.isArray(resp) ? resp : []);
    } catch (error) {
      console.log(error)
      setDetailError(
        error?.response?.data?.message || error?.message || 'No se pudo obtener el detalle.'
      );
      setDetailData([]);
    }
    setLoading(false);
    setShow(false);
    setDetailShow(true)



  };


  const handleChange = (e) => {
    console.log('handleChange')
    e.preventDefault();
    setFilter(e.target.value)
    const aux = filtered.filter(
      element => element.name.toLowerCase().trim().includes(e.target.value.toLowerCase().trim())

    )
    setDataFiltered(aux);

  }


  useEffect(() => {
    if (filtered == null) {
      setDataFiltered(null);
      return;
    }
    if (checked) {
      const aux = filtered.filter(
        element => element.documentos_no_autorizados > 0 ||
          element.no_recibidos > 0 ||
          element.nulos > 0 ||
          element.recibidos_con_errores > 0 ||
          element.documentos_reenviados > 0 ||
          element.con_error > 0
      );
      setDataFiltered(aux);
    } else {
      setDataFiltered(filtered);
    }
  }, [checked, filtered])

  function getFirstDayOfMonth(year, month) {
    return new Date(year, month, 1);
  }

  useEffect(() => {
    // console.log(yesterday);

    const firstDayCurrentMonth = getFirstDayOfMonth(
      yesterday.getFullYear(),
      yesterday.getMonth(),
    );

    setStartDate(firstDayCurrentMonth.toISOString().slice(0, 10));
    setEndDate(yesterday.toISOString().slice(0, 10));
    // console.log(firstDayCurrentMonth);

  }, [])



  // useEffect(() => {
  //   // console.log(yesterday);
  //  console.log('dsadsdsa');

  // }, [ExpandedComponent])

  const stats = useMemo(() => {
    if (!filtered) return null;
    const toNum = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : 0;
    };
    let problemas = 0;
    let noAutorizados = 0;
    let noRecibidosOEnviados = 0;
    let conErrores = 0;
    filtered.forEach((r) => {
      const na = toNum(r.documentos_no_autorizados);
      const nr = toNum(r.no_recibidos);
      const ne = toNum(r.nulos);
      const ce = toNum(r.recibidos_con_errores);
      noAutorizados += na;
      noRecibidosOEnviados += nr + ne;
      conErrores += ce;
      problemas += na + nr + ne + ce;
    });
    return { problemas, noAutorizados, noRecibidosOEnviados, conErrores };
  }, [filtered]);

  const countCell = (value, estado, row, variant = 'neutral') => {
    const n = value ?? 0;
    const clickable = n > 0;
    const cls = `count-cell count-cell--${clickable ? variant : 'zero'}`;
    return (
      <span
        className={cls}
        onClick={clickable ? () => handleButtonClick(row, estado) : undefined}
        role={clickable ? 'button' : undefined}
        title={clickable ? 'Ver detalle' : undefined}
      >
        {n}
      </span>
    );
  };

  const columns = [
    {
      name: 'Rut',
      selector: row => row.rut,
      sortable: true,
      width: '100px',
    },
    {
      name: 'Empresa',
      selector: row => row.name,
      sortable: true,
      wrap: true,
      grow: 2,
    },
    {
      name: 'Aceptados',
      selector: row => row.documentos_aceptados,
      cell: (row) => <span className="count-cell count-cell--success">{row.documentos_aceptados ?? 0}</span>,
      sortable: true,
      right: true,
      width: '110px',
    },
    {
      name: 'No autorizados',
      selector: row => row.documentos_no_autorizados ?? 0,
      cell: (row) => countCell(row.documentos_no_autorizados, 'No Autorizado', row, 'danger'),
      sortable: true,
      right: true,
      width: '130px',
    },
    {
      name: 'No recibidos',
      selector: row => row.no_recibidos ?? 0,
      cell: (row) => countCell(row.no_recibidos, 'No recibido', row, 'danger'),
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      name: 'No enviados',
      selector: row => row.nulos ?? 0,
      cell: (row) => countCell(row.nulos, 'isNull', row, 'warning'),
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      name: 'Con errores',
      selector: row => row.recibidos_con_errores ?? 0,
      cell: (row) => countCell(row.recibidos_con_errores, 'Recibido con errores', row, 'danger'),
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      name: 'Reenviados',
      selector: row => row.documentos_reenviados ?? 0,
      cell: (row) => countCell(row.documentos_reenviados, 'Reenviado', row, 'info'),
      sortable: true,
      right: true,
      width: '120px',
    },
    {
      // Intentos de emision web que fallaron antes de obtener folio. Esos
      // documentos quedan con estado ERROR, que es lo que pide el detalle.
      name: 'Sin emitir',
      selector: row => row.con_error ?? 0,
      cell: (row) => countCell(row.con_error, 'ERROR', row, 'warning'),
      sortable: true,
      right: true,
      width: '110px',
    },
    {
      // Documentos con folio de la empresa. No incluye "Sin emitir": esos nunca
      // llegaron a ser documentos.
      name: 'Total',
      selector: row => row.total ?? 0,
      cell: (row) => <span className="count-cell count-cell--zero">{row.total ?? 0}</span>,
      sortable: true,
      right: true,
      width: '100px',
    },
  ];



  const ExpandedComponent = ({ data }) => {
    // const [detailDocs, setdetailDocs] = useState(null);
    const [siiData, setSiiData] = useState(null);
    const [siiError, setSiiError] = useState('');

    const respPorDocumento = async (data) => {
      const { rut, folio, tipodocumento } = data;
      const params = { rut, folio, tipodocumento }
      const idToken = await getSession().then((session) => { return session.idToken.jwtToken });
      try {
        const resp = await consultaEstado(params, idToken);
        // setLoadingDetail(false);
        console.log(resp);
        var json = {
          "Descripcion": resp.descripcion,
          "Descripcion Error": resp.descripcionE,
          "Detalle": resp.detalle,
          "Fecha Recepción": resp.fecha_recepcion
        }
        console.log(json)
        setSiiData(json)
        // setdetailDocs(resp)

      } catch (error) {
        console.log(error);
        setSiiError(
          error?.response?.data?.message || error?.message || 'No se pudo consultar el estado en el SII.'
        );
      }

    }

    useEffect(() => {
      // Un documento con motivo_error nunca llego al SII, asi que preguntarle
      // por el solo devuelve "no existe". El motivo ya explica que paso.
      if (data?.motivo_error) return;
      respPorDocumento(data);
    }, [data]);


    // setLoadingDetail(false)
    console.log(data);
    if (data?.motivo_error) {
      return (
        <div className="result-grid" style={{ padding: '12px' }}>
          <div className="result-grid__row">
            <span className="result-grid__label">No se emitió</span>
            <span className="result-grid__value">{data.motivo_error}</span>
          </div>
          {data.document_uuid && (
            <div className="result-grid__row">
              <span className="result-grid__label">ID documento</span>
              <span className="result-grid__value">{data.document_uuid}</span>
            </div>
          )}
        </div>
      );
    }
    if (siiError) {
      return <div className="filter-form__error">{siiError}</div>;
    }
    let tieneData = (siiData != null);
    return (tieneData ? <pre>{JSON.stringify(siiData, null, 2)}</pre> : <pre> <div className="ml-2"><h4>Cargando{loadingDetail && <Spinner animation="border" role="status" className='sticky-top'></Spinner>}</h4> </div> </pre>);
    // return <pre> <div className="ml-2"><h4>Cargando{loadingDetail && <Spinner animation="border" role="status" className='sticky-top'></Spinner>}</h4> </div> </pre>
  }


  // useEffect(() => {
  //   console.log('datadetaaaaail')
  //   console.log(loadingDetail);
  //   // setLoadingDetail(false);
  // }, [detailDocs])
  // useEffect(()=> {
  //   console.log('useefeeeect')
  // },[ExpandedComponent]); 

  const columnsDetallesDoc = [
    {
      name: 'Folio',
      selector: row => row.folio,
      sortable: true,
      width: '8%',
    },
    {
      name: 'Origen',
      selector: row => row.origen ?? '',
      cell: (row) => (
        row.origen
          ? <span className={`origen-chip origen-chip--${row.origen}`}>{getOrigenLabel(row.origen)}</span>
          : <span className="dlq-count dlq-count--unknown">—</span>
      ),
      sortable: true,
      width: '9%'
    },
    {
      // Los documentos web no salen de un dispositivo: el backend manda null.
      name: 'Device_id',
      selector: row => row.device_id ?? '',
      cell: (row) => (
        row.device_id
          ? <span>{row.device_id}</span>
          : <span className="dlq-count dlq-count--unknown">—</span>
      ),
      sortable: true,
      width: '18%'
    },
    {
      name: 'RUT',
      selector: row => row.rut,
      sortable: true,
    },
    {
      name: 'Fecha',
      selector: row => row.fechaemision,
      sortable: true,
      width: '10%'
    },
    {
      name: 'Tipo Documento',
      selector: row => row.tipodocumento,
      // cell: (row) => <div style={{ width: '18rem', cursor: 'pointer' }} onClick={() => { handleButtonClick(row) }}>{row.documentos_no_autorizados}</div>,
      sortable: true,
      width: '12%'

    },
    {
      name: 'Valor Total',
      selector: row => row.valortotal,
      sortable: true,
      width: '10%'
    },
    {
      name: 'Valor Neto',
      selector: row => row.valorneto,
      sortable: true,
      width: '10%'
    },
    {
      name: 'Valor I.V.A',
      selector: row => row.valoriva,
      sortable: true,
      width: '10%'
    },
    {
      // Solo llega en filas web que fallaron antes de obtener folio. No es una
      // respuesta del SII: ese documento nunca se envio.
      name: 'Motivo error',
      selector: row => row.motivo_error ?? '',
      cell: (row) => (
        row.motivo_error
          ? <span className="dlq-badge dlq-badge--error" title={row.motivo_error}>{row.motivo_error}</span>
          : <span className="dlq-count dlq-count--unknown">—</span>
      ),
      sortable: true,
      wrap: true,
      width: '18%'
    },
    // {

    //   cell: (row) => <Button color="secondary" onClick={() => { Reenvio(row) }} >Reenviar</Button>,
    //   ignoreRowClick: true,
    //   allowOverflow: true,
    //   button: true,
    //   width: '12%'
    // },
  ];
  const paginationOpciones = {
    rowsPerPageText: 'Filas por página',
    rangeSeparatorText: 'de',
    selectAllRowsItem: true,
    selectAllRowsText: 'Todos'
  }

  // Vuelve a pedir el resumen con los filtros actuales, sin abrir el modal de
  // carga: se usa despues de un reenvio para que los contadores reflejen el
  // cambio de estado. Si falla, la tabla queda como estaba.
  const refrescarResumen = async (idToken) => {
    try {
      const resp = await consultaEstadoDocs(cleanParams({ startDate, endDate, tipodocumento }), idToken);
      setFiltered(resp);
    } catch (error) {
      console.log(error);
    }
  };

  // Los fallidos traen un motivo por documento; el de S3 incluye el prefijo
  // buscado, que es distinto en cada uno. Sin el prefijo se pueden agrupar.
  const resumirFallidos = (fallidos) => {
    const porMotivo = new Map();
    fallidos.forEach((f) => {
      const motivo = String(f.motivo || 'Motivo desconocido').replace(/\s*\(prefijo[^)]*\)/, '');
      if (!porMotivo.has(motivo)) porMotivo.set(motivo, []);
      porMotivo.get(motivo).push(f.folio);
    });
    return [...porMotivo.entries()].map(([motivo, folios]) => {
      const muestra = folios.slice(0, 10).join(', ') + (folios.length > 10 ? '…' : '');
      return `• ${motivo}: ${folios.length} (folios ${muestra})`;
    });
  };

  const Reenvio = async ({ rut, selectedList }) => {
    setDetailShow(false);
    setConfirmacion(null);
    setmodalTitle('Reenvío de documentos');
    setMessage('');
    setLoading(true);
    setShow(true);

    const total = selectedList.length;
    const tandas = [];
    for (let i = 0; i < total; i += TANDA_REENVIO) {
      tandas.push(selectedList.slice(i, i + TANDA_REENVIO));
    }
    setTotalReenvio(total);
    setTandasReenvio(armarTandas(total));
    const marcarTanda = (n, cambios) =>
      setTandasReenvio((prev) => prev.map((t) => (t.n === n ? { ...t, ...cambios } : t)));

    let reenviados = 0;
    const fallidos = [];
    // Tanda que no respondio (timeout de API Gateway, red). No se sabe si la
    // lambda la proceso, asi que no se reintenta ni se sigue con las demas.
    let cortada = null;
    let idToken = null;

    try {
      // getSession dentro del try: si falla la sesion, el modal tambien tiene
      // que salir del estado "cargando" en vez de quedarse girando.
      idToken = await getSession().then((session) => { return session.idToken.jwtToken });

      for (let t = 0; t < tandas.length; t++) {
        const desde = t * TANDA_REENVIO;
        const n = t + 1;
        marcarTanda(n, { estado: 'enviando' });
        // Lo que devolvió la tanda: la respuesta normal, o el 500 del backend
        // cuando no pudo reenviar ninguno pero trae el detalle de cada fallo
        // (eso es un resultado, no un corte).
        let resultado = null;
        try {
          const resp = await reenvioDocs({ rut, selectedList: tandas[t] }, idToken);
          resultado = resp?.data || {};
        } catch (error) {
          const cuerpo = error?.response?.data;
          if (cuerpo && Array.isArray(cuerpo.fallidos)) {
            resultado = cuerpo;
          } else {
            console.log(error);
            cortada = { desde: desde + 1, hasta: desde + tandas[t].length };
            // Sin respuesta no se sabe si la lambda la procesó: no se reintenta
            // ni se sigue con las demás.
            marcarTanda(n, { estado: 'sin_respuesta' });
            setTandasReenvio((prev) => prev.map((x) => (x.n > n ? { ...x, estado: 'no_enviada' } : x)));
            break;
          }
        }
        const okTanda = Number(resultado.reenviados) || 0;
        const fallidosTanda = resultado.fallidos || [];
        reenviados += okTanda;
        fallidos.push(...fallidosTanda);
        marcarTanda(n, {
          estado: fallidosTanda.length ? 'con_fallidos' : 'ok',
          reenviados: okTanda,
          fallidos: fallidosTanda.length,
        });
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      setTandasReenvio([]);
      setMessage(error?.message || 'No se pudo obtener la sesión para reenviar.');
      return;
    }

    const lineas = [`${reenviados} de ${total} documento${total === 1 ? '' : 's'} reenviado${reenviados === 1 ? '' : 's'}.`];
    if (fallidos.length > 0) {
      lineas.push('', `${fallidos.length} no se pudieron reenviar:`, ...resumirFallidos(fallidos));
    }
    if (cortada) {
      const pendientes = total - cortada.hasta;
      lineas.push(
        '',
        `Los documentos ${cortada.desde} a ${cortada.hasta} no recibieron respuesta a tiempo. ` +
        'Pudieron haberse reenviado igual: revisa su estado antes de volver a reenviarlos.',
      );
      if (pendientes > 0) lineas.push(`Los ${pendientes} restantes no se enviaron.`);
    }

    setSelectedRows([]);
    setToggleCleared((v) => !v);
    setLoading(false);
    setMessage(lineas.join('\n'));
    if (reenviados > 0 || cortada) await refrescarResumen(idToken);
  }

  const handleCheckTable = ({ selectedRows }) => {
    // You can set state or dispatch with something like Redux so we can use the retrieved data
    // console.log('Selected Rows: ', selectedRows);
    setSelectedRows(selectedRows)
    // Si la selección cambia con la confirmación abierta, la confirmación
    // hablaría de otros documentos: se cierra y hay que volver a pedirla.
    setConfirmacion(null);
  };

  const handleReenviarSeleccionados = async () => {
    if (!selectedRows || selectedRows.length === 0) return;
    // origen le permite al backend explicar por que un documento web no se
    // reenvia por este flujo (su XML vive en otro bucket, con otra key); sin el,
    // lo reportaba como "no se encontro el documento en S3".
    const selectedList = selectedRows.map((element) => ({
      folio: element.folio,
      fechaemision: element.fechaemision,
      tipodocumento: element.tipodocumento,
      origen: element.origen,
    }));
    const rut = selectedRows[0].rut;
    // Hasta una tanda se manda directo, como siempre. Con más, primero se
    // muestra el plan (cuántas tandas, qué documentos van en cada una y cuánto
    // tarda) y recién se envía cuando la persona lo confirma: reenviar dos
    // veces el mismo lote manda dos veces los mismos documentos al SII.
    if (cantidadTandas(selectedList.length) > 1) {
      setConfirmacion({ rut, selectedList });
      return;
    }
    await Reenvio({ rut, selectedList });
  };

  const limpiarFiltros = () => {
    setTipodocumento("");
    setStartDate("");
    setEndDate("");
  };

  return (
    <>
      <Container fluid className="page-container">
        <div className="page-header">
          <div>
            <h2 className="page-title">Resumen Estado Docs</h2>
            <p className="page-subtitle">
              Consulta el estado consolidado de documentos por empresa en un rango de fechas.
            </p>
          </div>
        </div>

        <Card className="data-card">
          <Card.Body>
            <Form onSubmit={busqueda} className="filter-form">
              <Row>
                <Col sm={12} md={4}>
                  <Form.Group className="form-field">
                    <Form.Label>Tipo documento</Form.Label>
                    <Select
                      options={TIPOS_DOCUMENTO_CON_TODOS}
                      value={TIPOS_DOCUMENTO_CON_TODOS.find((o) => o.value === tipodocumento) || null}
                      onChange={(opt) => setTipodocumento(opt ? opt.value : '')}
                      isSearchable={false}
                      classNamePrefix="rs"
                    />
                  </Form.Group>
                </Col>
                <Col sm={6} md={3}>
                  <Form.Group className="form-field">
                    <Form.Label>Desde</Form.Label>
                    <FormControl type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col sm={6} md={3}>
                  <Form.Group className="form-field">
                    <Form.Label>Hasta</Form.Label>
                    <FormControl type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
                  </Form.Group>
                </Col>
                <Col sm={12} md={2} className="d-flex align-items-end form-actions">
                  <Button type="submit" className="btn-primary-action">
                    <i className="nc-icon nc-zoom-split" />
                    Buscar
                  </Button>
                </Col>
              </Row>
              <div className="filter-form__footer">
                <Button className="btn-link-clear" onClick={limpiarFiltros}>
                  Limpiar filtros
                </Button>
                {dataFiltered != null && (
                  <label className="check-inline">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => setChecked(e.currentTarget.checked)}
                    />
                    <span>Mostrar solo con errores</span>
                  </label>
                )}
              </div>
            </Form>
          </Card.Body>
        </Card>

        {stats && (
          <Row className="stats-row">
            <Col xs={12} sm={6} lg={3}>
              <div className="stat-card stat-card--danger">
                <div className="stat-card__icon"><i className="nc-icon nc-simple-remove" /></div>
                <div className="stat-card__content">
                  <span className="stat-card__label">Total con problemas</span>
                  <span className="stat-card__value">{stats.problemas}</span>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <div className="stat-card stat-card--danger">
                <div className="stat-card__icon"><i className="nc-icon nc-lock-circle-open" /></div>
                <div className="stat-card__content">
                  <span className="stat-card__label">No autorizados</span>
                  <span className="stat-card__value">{stats.noAutorizados}</span>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <div className="stat-card stat-card--warning">
                <div className="stat-card__icon"><i className="nc-icon nc-cloud-upload-94" /></div>
                <div className="stat-card__content">
                  <span className="stat-card__label">No recibidos / enviados</span>
                  <span className="stat-card__value">{stats.noRecibidosOEnviados}</span>
                </div>
              </div>
            </Col>
            <Col xs={12} sm={6} lg={3}>
              <div className="stat-card stat-card--warning">
                <div className="stat-card__icon"><i className="nc-icon nc-alert-circle-i" /></div>
                <div className="stat-card__content">
                  <span className="stat-card__label">Con errores</span>
                  <span className="stat-card__value">{stats.conErrores}</span>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {dataFiltered != null && (
          <Card className="data-card">
            <Card.Body>
              <div className="data-card__toolbar">
                <div className="search-input">
                  <i className="nc-icon nc-zoom-split" />
                  <input
                    type="text"
                    name="search"
                    value={filter}
                    autoComplete="off"
                    placeholder="Filtrar por empresa..."
                    onChange={handleChange}
                  />
                </div>
                <Button className="btn-export" onClick={() => downloadCSV(dataFiltered)}>
                  <i className="nc-icon nc-cloud-download-93" />
                  Exportar CSV
                </Button>
              </div>
              <DataTable
                className="-striped -highlight"
                fixedHeader={true}
                columns={columns}
                data={dataFiltered}
                pagination
                paginationComponentOptions={paginationOpciones}
              />
            </Card.Body>
          </Card>
        )}

        {/* Mientras se reenvía por tandas el modal no se puede cerrar: cerrarlo
            escondía el avance pero el envío seguía corriendo por detrás. */}
        <Modal
          show={show}
          onHide={handleClose}
          centered
          size={tandasReenvio.length > 0 ? 'lg' : undefined}
          backdrop={loading && tandasReenvio.length > 0 ? 'static' : true}
          keyboard={!(loading && tandasReenvio.length > 0)}
        >
          <Modal.Header closeButton={!(loading && tandasReenvio.length > 0)}>
            <Modal.Title>{modalTitle}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {tandasReenvio.length > 0 ? (
              <>
                <ListaTandas tandas={tandasReenvio} total={totalReenvio} conBarra />
                {loading && (
                  <div style={{ marginTop: 12, fontSize: 12.5, color: '#64748b' }}>
                    No cierres esta ventana hasta que terminen todas las tandas.
                  </div>
                )}
                {!loading && Message && (
                  <div style={{ marginTop: 14, whiteSpace: 'pre-line', fontSize: 13.5, color: '#1e293b' }}>{Message}</div>
                )}
              </>
            ) : (
              <div className="result-loading">
                {loading && <Spinner animation="border" role="status" />}
                <span style={{ whiteSpace: 'pre-line' }}>{Message}</span>
              </div>
            )}
          </Modal.Body>
          {!loading && (
            <Modal.Footer>
              <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
            </Modal.Footer>
          )}
        </Modal>

        <Modal show={detailShow} onHide={handleCloseDetail} size="xl" scrollable centered>
          <Modal.Header closeButton>
            <Modal.Title>Detalle Documentos</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Card className="data-card">
              <Card.Body>
                <div className="data-card__toolbar">
                  <span className="toolbar-meta">
                    {detailData ? `${detailData.length} documento${detailData.length === 1 ? '' : 's'}` : ''}
                    {selectedRows && selectedRows.length > 0 && (
                      <span className="toolbar-meta__selected">
                        · {miles(selectedRows.length)} seleccionado{selectedRows.length === 1 ? '' : 's'}
                        {cantidadTandas(selectedRows.length) > 1 && (
                          <> · se reenvían en {cantidadTandas(selectedRows.length)} tandas de hasta {TANDA_REENVIO}</>
                        )}
                      </span>
                    )}
                  </span>
                  <div className="toolbar-actions">
                    {selectedRows && selectedRows.length > 0 && !confirmacion && (
                      <Button className="btn-primary-action btn-reenviar" onClick={handleReenviarSeleccionados}>
                        <i className="nc-icon nc-send" />
                        {cantidadTandas(selectedRows.length) > 1
                          ? `Reenviar en ${cantidadTandas(selectedRows.length)} tandas`
                          : 'Reenviar seleccionados'}
                      </Button>
                    )}
                    <Button
                      className="btn-export"
                      onClick={() => downloadCSV(detailData)}
                      disabled={!detailData || detailData.length === 0}
                    >
                      <i className="nc-icon nc-cloud-download-93" />
                      Exportar CSV
                    </Button>
                  </div>
                </div>
                {/* Confirmación del reenvío por tandas, dentro del mismo modal:
                    abrir otro encima desmontaba la tabla y se perdía la selección. */}
                {confirmacion && (
                  <div style={{
                    border: `1px solid ${AZUL_NIEBLA}`, borderLeft: `4px solid ${AZUL}`, borderRadius: 8,
                    background: '#f7f9fc', padding: '14px 16px', margin: '4px 0 16px',
                  }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: AZUL, marginBottom: 4 }}>
                      Reenviar {miles(confirmacion.selectedList.length)} documentos en {cantidadTandas(confirmacion.selectedList.length)} tandas
                    </div>
                    <div style={{ fontSize: 13, color: '#475569', marginBottom: 12, lineHeight: 1.5 }}>
                      Se mandan de a {TANDA_REENVIO}, una tanda después de la otra: así ninguna llamada pasa el
                      límite de 29 segundos. Cada tanda tarda unos 9 segundos (en total, cerca
                      de {Math.max(1, Math.round((confirmacion.selectedList.length * 0.045) / 60))} minuto{Math.round((confirmacion.selectedList.length * 0.045) / 60) > 1 ? 's' : ''}).
                      Si una tanda no responde, se detiene ahí y te dice exactamente cuáles quedaron sin enviar.
                    </div>
                    <ListaTandas tandas={armarTandas(confirmacion.selectedList.length)} total={confirmacion.selectedList.length} />
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 14 }}>
                      <Button variant="secondary" onClick={() => setConfirmacion(null)}>Cancelar</Button>
                      <Button className="btn-primary-action btn-primary-action--inline" onClick={() => Reenvio(confirmacion)}>
                        <i className="nc-icon nc-send" />
                        Reenviar en {cantidadTandas(confirmacion.selectedList.length)} tandas
                      </Button>
                    </div>
                  </div>
                )}
                {detailError ? (
                  <div className="filter-form__error">{detailError}</div>
                ) : !detailData || detailData.length === 0 ? (
                  <div className="no-data-message">
                    No hay documentos para este estado en el rango consultado.
                  </div>
                ) : (
                  <DataTable
                    className="-striped -highlight"
                    fixedHeader={true}
                    columns={columnsDetallesDoc}
                    data={detailData}
                    pagination
                    expandableRows={true}
                    expandOnRowClicked={true}
                    selectableRows
                    clearSelectedRows={toggleCleared}
                    onSelectedRowsChange={handleCheckTable}
                    expandableRowsComponent={ExpandedComponent}
                    paginationComponentOptions={paginationOpciones}
                  />
                )}
              </Card.Body>
            </Card>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDetail}>Cerrar</Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
}

export default EstadoResumen;
